import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import type { CalendarConnection, CalendarProvider, UpdateLinkedCalendarPayload } from '../types';

// Calendar connections are user-scoped, never tenant-scoped — every call opts
// out of X-Tenant-Id (same pattern as notes.service.ts).
const NO_TENANT = { skipTenantHeader: true } as const;

/** Manual sync is synchronous server-side and can take 30-60s on a cold cursor. */
const SYNC_TIMEOUT_MS = 120_000;

export type CalendarProviderSlug = 'google' | 'microsoft';

export const PROVIDER_TO_SLUG: Record<CalendarProvider, CalendarProviderSlug> = {
  Google: 'google',
  Microsoft: 'microsoft',
};

export const calendarConnectionsService = {
  getConnections: async (signal?: AbortSignal): Promise<CalendarConnection[]> => {
    const { data } = await httpClient.get<CalendarConnection[]>(`${API_V1}/calendar/connections`, {
      ...NO_TENANT,
      signal,
    });
    return data;
  },

  getAuthorizationUrl: async (
    provider: CalendarProviderSlug,
    returnPath: string,
    signal?: AbortSignal,
  ): Promise<string> => {
    // withCredentials: the response also sets an anti account-linking-CSRF
    // cookie (backend khronox-be-v3@76306fb) — without it the browser
    // discards the cookie and every connection attempt fails with
    // CalendarOAuth.InvalidState. Scoped to this call only, not global.
    const { data } = await httpClient.get<{ authorizationUrl: string }>(
      `${API_V1}/calendar/connections/${provider}/authorize`,
      { ...NO_TENANT, params: { returnPath }, withCredentials: true, signal },
    );
    return data.authorizationUrl;
  },

  deleteConnection: async (connectionId: string): Promise<void> => {
    await httpClient.delete(`${API_V1}/calendar/connections/${connectionId}`, undefined, NO_TENANT);
  },

  updateLinkedCalendar: async (
    connectionId: string,
    calendarId: string,
    payload: UpdateLinkedCalendarPayload,
  ): Promise<void> => {
    await httpClient.patch(
      `${API_V1}/calendar/connections/${connectionId}/calendars/${calendarId}`,
      payload,
      NO_TENANT,
    );
  },

  syncConnection: async (connectionId: string, signal?: AbortSignal): Promise<void> => {
    await httpClient.post(`${API_V1}/calendar/connections/${connectionId}/sync`, undefined, {
      ...NO_TENANT,
      timeout: SYNC_TIMEOUT_MS,
      signal,
    });
  },
};
